import type { ReactNode } from 'react';

/**
 * A foto que veio do catálogo da loja — usada pelo APP da cliente e pelo
 * PAINEL da lojista.
 *
 * ⚠️ Este é o primeiro arquivo compartilhado entre os dois fronts. Eles são
 * aplicações separadas de propósito (públicos diferentes, builds diferentes,
 * vocabulário de cor diferente), e nada de layout deve descer para cá. O que
 * mora aqui é a decisão técnica que precisa ser IGUAL nos dois: quando uma
 * imagem de fora quebra, o que aparece no lugar.
 *
 * Cada tela desenha o próprio `vazio`. É de propósito: o hachurado com a
 * legenda "foto" na lojinha, o "sem foto" com dica na escada de prêmios e o
 * "foto grande" do detalhe são três desenhos diferentes, e um componente que
 * tentasse servir os três com chaves liga-desliga seria pior que as cópias.
 */
export function Foto({
  capa,
  alt,
  className,
  vazio,
}: {
  /** A url. `null` (ou vazia) significa "esta peça não tem foto", e é normal. */
  capa: string | null;
  /** Vazio quando a foto é decorativa e o nome já está escrito ao lado. */
  alt: string;
  className?: string;
  /** O que ocupa o lugar quando não há foto. */
  vazio: ReactNode;
}): ReactNode {
  if (!capa) return vazio;

  return (
    <img
      className={className}
      src={capa}
      alt={alt}
      /*
       * A lojinha desenha a grade inteira de uma vez e a cliente abre isso no
       * celular, muitas vezes no 4G da fila do caixa: as fotos fora da tela não
       * precisam custar dados antes de serem vistas.
       */
      loading="lazy"
      /*
       * ⚠️ Link de imagem QUEBRA na vida real — a loja troca de CDN, apaga a
       * foto, o catálogo sincroniza uma url velha. Sem isto a tela mostra o
       * ícone de papel rasgado do navegador, que parece defeito do nosso app.
       *
       * `visibility` e não `display`: escondendo o elemento sem tirá-lo do
       * fluxo, o espaço continua reservado. No app aparece o hachurado do
       * contêiner atrás; no painel a escada de prêmios não perde 46px e
       * desalinha a linha inteira.
       */
      onError={(e) => {
        e.currentTarget.style.visibility = 'hidden';
      }}
    />
  );
}
